import React, {
  useCallback,
  useMemo,
  useRef,
  useEffect,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { Alert, Button, Row, Col, Nav, NavItem, NavLink, Badge, Card, Table, Spinner, Form } from "react-bootstrap";
import { AgGridReact } from "ag-grid-react";
import { GridReadyEvent, IFilterDef, RowClickedEvent } from "ag-grid-community";
import { useHistory, useLocation } from "react-router-dom";
import { confirmAlert } from "react-confirm-alert";
import { setSelectedInvoiceId } from "../../../redux/reducers/TagLevel";
import {
  getInvoicesTagLevels,
  getInvoiceWorkflowSummaryTagLevels,
  getInvoiceWorkflowTagLevels,
  getInvoiceAnalyticsTagLevels,
  loadContractPreferenceServer,
  saveContractdPreferenceServer,
  getJobTransactions2,
  getJobTransactions2TagLevels,
  deleteTransactionLine,
} from "../../../docuclipper/api";
import { getCustomUi } from "../../Docuclipper/ConfirmAlert/ConfirmAlert";
import TourMenuButton from "../../Docuclipper/Tour/TourMenuButton";
import { ReduxState } from "../../../redux";
import ExternalFilter from "src/views/Docuclipper/ExternalFilter";
import {
  setExternalFiltersColumnDefs,
  clearExternalFilters,
} from "src/redux/reducers/ExternalFilter";
import { TableFilterConfig } from "src/docuclipper/DocuclipperTypes";
import { datefilterParamsYYYYMMDD } from "../../Pages/Analyze/AnalyzeTransactionsTable";
import { createAlert } from "src/redux/reducers/Alerts";
import { convertFilterModel, convertSortModel } from "src/utils/filters";
import AddDocumentsModal from "../DataArchive/TransactionManager/AddDocumentsModal";
import AddDocumentsPopover from "../DataArchive/TransactionManager/AddBankStatementsPdfCsvPopover";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { setDownloadInvoiceModalOpen } from "../../../redux/reducers/JobExport";
import { DownloadInvoiceModal } from "../DataArchive/TransactionManager/DownloadInvoiceModal";
import { useRecentDocument } from "src/hooks/useRecentNavigation";
import { SUPPORTED_SCOPES, fetchTagLevelIntegration } from "src/redux/reducers/TagLevelIntegration";
import { fetchIntegrations } from "src/redux/reducers/Integration";
import { fetchTagLevels } from "src/redux/reducers/TagLevel";
import InvoicePage from "../Invoices/InvoicePage";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  BarChart,
  Bar,
} from "recharts";

const getType = (documentType) => {
  if (["payables", "bill", "expense"].includes(documentType)) {
    return "payables";
  } else if (["receivables", "invoice"].includes(documentType)) {
    return "receivables";
  }
  throw new Error(`Unknown document type: ${documentType}`);
};

const formatData = (data) => {
  // Ensure data is an array before calling map
  if (!Array.isArray(data)) {
    console.warn("formatData received non-array data:", data);
    return [];
  }

  return data.map((item) => {
    // Parse invoice row to check sync status
    let invoiceRow = null;
    let isSynced = false;
    try {
      if (item.row) {
        invoiceRow = typeof item.row === 'string' ? JSON.parse(item.row) : item.row;
        isSynced = !!(invoiceRow?.link || invoiceRow?.status === "synced" || invoiceRow?.status === "imported");
      }
    } catch (e) {
      // Ignore parse errors
    }

    // Check metadata for sync info
    let metadataSyncInfo = null;
    try {
      if (item.metadata) {
        const metadata = typeof item.metadata === 'string' ? JSON.parse(item.metadata) : item.metadata;
        if (metadata?.integrations) {
          // Check if any integration has synced
          const integrations = metadata.integrations;
          metadataSyncInfo = Object.values(integrations).some((integration: any) => 
            integration?.syncedAt || integration?.id
          );
        }
      }
    } catch (e) {
      // Ignore parse errors
    }

    return {
      id: item.transactionLineId,
      invoiceId: item.invoiceId,
      invoiceDate: item.documentDate,
      invoiceVendor: item.vendorNormalized || "",
      invoiceCustomer: item.customerNormalized || "",
      invoiceAmount: parseFloat(item.amount) || 0,
      invoiceType: getType(item.documentType),
      docType: item.documentType,
      approvalStatus: item.approvalStatus || "",
      needsApproval: item.needsApproval || false,
      isSynced: isSynced || metadataSyncInfo || false,
      hasPOMatch: String(Boolean(item.hasPOMatch)),
      hasBankMatch: String(Boolean(item.hasBankMatch)),
    };
  });
};

type TabType = "all" | "needsApproval" | "notSynced";
type ViewMode = "invoices" | "inbox" | "analytics";
type InboxTabType = "inbox" | "exceptions" | "approved" | "all";

interface InvoicesProps {
  forcedViewMode?: ViewMode;
  showModeToggle?: boolean;
}

export const Invoices: React.FC<InvoicesProps> = ({
  forcedViewMode,
  showModeToggle = true,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const gridApi = useRef<any>(null);
  const editingInvoiceId = new URLSearchParams(location.search).get("invoiceId");
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    totalCount: 0,
    totalPages: 0,
  });
  const [hasData, setHasData] = useState<boolean | null>(null); // null = loading, true = has data, false = empty
  const [activeTab, setActiveTab] = useState<TabType>("all");
  const [viewMode, setViewMode] = useState<ViewMode>(forcedViewMode || "invoices");
  const [activeInboxTab, setActiveInboxTab] = useState<InboxTabType>("inbox");
  const [inboxRows, setInboxRows] = useState<any[]>([]);
  const [inboxLoading, setInboxLoading] = useState(false);
  const [inboxError, setInboxError] = useState<string | null>(null);
  const [inboxSummaryData, setInboxSummaryData] = useState({
    needsReviewCount: 0,
    exceptionsCount: 0,
    overdueCount: 0,
    totalPendingAmount: 0,
    firstTimeIssueCount: 0,
  });
  const [apQueueConfig, setApQueueConfig] = useState({
    ignore_po_matching: false,
    ignore_bank_matching: false,
  });
  const [savingApQueueConfig, setSavingApQueueConfig] = useState<{
    ignore_po_matching: boolean;
    ignore_bank_matching: boolean;
  }>({
    ignore_po_matching: false,
    ignore_bank_matching: false,
  });
  const [showInboxEntryMessage, setShowInboxEntryMessage] = useState(false);
  const [modePreferenceLoaded, setModePreferenceLoaded] = useState(Boolean(forcedViewMode));
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState<string | null>(null);
  const [analyticsInterval, setAnalyticsInterval] = useState<"week" | "month">("month");
  const [analyticsStartDate, setAnalyticsStartDate] = useState("");
  const [analyticsEndDate, setAnalyticsEndDate] = useState("");
  const [analyticsData, setAnalyticsData] = useState<any | null>(null);
  const [tabCounts, setTabCounts] = useState<{
    all: number | null;
    needsApproval: number | null;
    notSynced: number | null;
  }>({
    all: null,
    needsApproval: null,
    notSynced: null,
  });

  const { externalFilters } = useSelector(
    (state: ReduxState) => state.ExternalFilter
  );
  const user = useSelector((state: ReduxState) => state.User.user);
  const isContractAdmin = Boolean(user?.isContractAdmin);
  const job = useSelector((state: ReduxState) => state.JobData.job);

  const { selectedL1: l1, selectedL2: l2, tagLevels, loading: tagLevelsLoading } = useSelector(
    (state: ReduxState) => state.TagLevel
  );

  const { integrations, loading: integrationsLoading } = useSelector(
    (state: ReduxState) => state.Integration
  );

  const linkedIntegrationDetails = useSelector(
    (state: ReduxState) => state.TagLevelIntegration.linkedIntegrationDetails
  );

  // Get current tagLevelId from l1/l2
  const currentTagLevelId = useMemo(() => {
    if (!l1 || !l2 || !tagLevels?.root) return null;
    return (tagLevels.root as any)?.[l1]?.[l2]?.metadata?.tagId || null;
  }, [l1, l2, tagLevels]);

  // Fetch integrations and tag levels on mount
  useEffect(() => {
    dispatch(fetchIntegrations());
    dispatch(fetchTagLevels());
  }, [dispatch]);

  // Fetch integration for current tag level (after integrations and tag levels are loaded).
  // Re-run when integrations load so we can resolve the link (e.g. first load had empty integrations).
  useEffect(() => {
    if (!integrationsLoading && !tagLevelsLoading && currentTagLevelId) {
      dispatch(fetchTagLevelIntegration());
    }
  }, [currentTagLevelId, dispatch, integrationsLoading, tagLevelsLoading, integrations.length]);

  // Check if integration is connected (Xero or QuickBooks)
  const hasIntegration = useMemo(() => {
    return !!(
      linkedIntegrationDetails?.scope === SUPPORTED_SCOPES.xero ||
      linkedIntegrationDetails?.scope === SUPPORTED_SCOPES.quickbooks
    );
  }, [linkedIntegrationDetails]);

  // Fetch counts for all tabs when l1/l2 changes
  useEffect(() => {
    if (!l1 || !l2) return;

    // Fetch count for "all" tab
    getInvoicesTagLevels(l1, l2, {
      pagination: { page: 1, pageSize: 1 },
      filters: [],
      sorting: [],
    })
      .then((response) => {
        setTabCounts((prev) => ({
          ...prev,
          all: response.pagination.totalCount,
        }));
      })
      .catch((err) => {
        console.error("Error fetching all invoices count:", err);
      });

    // Fetch count for "needsApproval" tab
    // We fetch pending status and use that as the count
    // The actual count (including other non-approved statuses) will be updated when the tab is visited
    getInvoicesTagLevels(l1, l2, {
      pagination: { page: 1, pageSize: 1 },
      filters: [
        {
          field: "approvalStatus",
          operator: "equals",
          value: "pending",
        },
      ],
      sorting: [],
    })
      .then((response) => {
        // Use pending count as initial approximation
        // Will be refined when tab is visited with client-side filtering
        setTabCounts((prev) => ({
          ...prev,
          needsApproval: response.pagination.totalCount,
        }));
      })
      .catch((err) => {
        console.error("Error fetching needs approval count:", err);
      });

    // Fetch count for "notSynced" tab (only if integration exists)
    if (hasIntegration) {
      getInvoicesTagLevels(l1, l2, {
        pagination: { page: 1, pageSize: 1 },
        filters: [
          {
            field: "isSynced",
            operator: "equals",
            value: false,
          },
        ],
        sorting: [],
      })
        .then((response) => {
          setTabCounts((prev) => ({
            ...prev,
            notSynced: response.pagination.totalCount,
          }));
        })
        .catch((err) => {
          console.error("Error fetching not synced count:", err);
        });
    }
  }, [l1, l2, hasIntegration]);

  useEffect(() => {
    if (forcedViewMode) {
      setViewMode(forcedViewMode);
      setModePreferenceLoaded(true);
      return;
    }
  }, [forcedViewMode]);

  useEffect(() => {
    if (forcedViewMode) {
      return;
    }
    const loadModePreference = async () => {
      try {
        const preference = await loadContractPreferenceServer({
          key: "invoiceExperienceMode",
        });
        const value = String(preference?.value || "").toLowerCase();
        if (value === "inbox" || value === "invoices" || value === "analytics") {
          setViewMode(value as ViewMode);
        }
      } catch (error) {
        // Silent fallback to default mode
      } finally {
        setModePreferenceLoaded(true);
      }
    };
    loadModePreference();
  }, [forcedViewMode]);

  // Track this page view as a recent document
  useRecentDocument(
    l1 && l2
      ? {
          docType: "invoices",
          label: `Invoices - ${l2}`,
          icon: "file-invoice",
          l1: l1,
          l2: l2,
        }
      : null
  );

  const types = [
    {
      label: "Receivables / Invoices",
      value: "receivables",
    },
    {
      label: "Payables / Bills / Expenses",
      value: "payables",
    },
  ];

  const docTypes = [
    {
      label: "Payables",
      value: "payables",
    },
    {
      label: "Bill",
      value: "bill",
    },
    {
      label: "Expense",
      value: "expense",
    },
    {
      label: "Receivables",
      value: "receivables",
    },
    {
      label: "Invoice",
      value: "invoice",
    },
  ];

  const externalFilterColumns = useMemo(
    () => [
      {
        field: "invoiceId",
        label: "Invoice ID",
        filter: true,
        filterType: "text",
        defaultFilterType: "contains",
        configurable: true,
      },
      {
        field: "invoiceDate",
        label: "Date",
        filter: true,
        filterType: "date",
        defaultFilterType: "equals",
        configurable: true,
      },
      {
        field: "invoiceAmount",
        label: "Amount",
        filter: true,
        filterType: "number",
        defaultFilterType: "equals",
        configurable: true,
      },
      {
        field: "invoiceType",
        label: "Type",
        filter: true,
        filterType: "select",
        options: types,
        defaultFilterType: "equals",
        configurable: true,
      },
      {
        field: "invoiceVendor",
        label: "Vendor",
        filter: true,
        filterType: "text",
        defaultFilterType: "contains",
        configurable: true,
      },
      {
        field: "invoiceCustomer",
        label: "Customer",
        filter: true,
        filterType: "text",
        defaultFilterType: "contains",
        configurable: true,
      },
      {
        field: "approvalStatus",
        label: "Approval Status",
        filter: true,
        filterType: "select",
        options: [
          { label: "Pending", value: "pending" },
          { label: "Approved", value: "approved" },
        ],
        defaultFilterType: "equals",
        configurable: true,
      },
      {
        field: "hasPOMatch",
        label: "Has PO Match",
        filter: true,
        filterType: "select",
        options: [
          { label: "Yes", value: "true" },
          { label: "No", value: "false" },
        ],
        defaultFilterType: "equals",
        configurable: true,
      },
      {
        field: "hasBankMatch",
        label: "Has Bank Match",
        filter: true,
        filterType: "select",
        options: [
          { label: "Yes", value: "true" },
          { label: "No", value: "false" },
        ],
        defaultFilterType: "equals",
        configurable: true,
      },
    ],
    []
  );

  const getRows = useCallback(
    (params) => {
      if (!l1 || !l2) {
        // console.log("missing l1 or l2");
        params.failCallback();
        setHasData(false);
        return;
      }

      // Build filters based on active tab
      const tabFilters = [];
      
      if (activeTab === "needsApproval") {
        // Filter for invoices that need approval (status is pending or not approved)
        // We'll filter for pending status, and also check client-side for other non-approved statuses
        tabFilters.push({
          field: "approvalStatus",
          operator: "equals",
          value: "pending",
        });
      } else if (activeTab === "notSynced" && hasIntegration) {
        // Filter for invoices that haven't synced
        tabFilters.push({
          field: "isSynced",
          operator: "equals",
          value: false,
        });
      }

      try {
        getInvoicesTagLevels(l1, l2, {
          pagination: {
            page: Math.floor(params.startRow / pagination.pageSize) + 1,
            pageSize: params.endRow - params.startRow,
          },
          filters: [
            ...convertFilterModel(params),
            ...tabFilters,
          ],
          sorting: convertSortModel(params.sortModel),
        })
          .then((response) => {
            // console.log({ invoiceData: response.data });
            let formattedData = formatData(response.data);
            
            // Apply client-side filtering based on active tab
            if (activeTab === "needsApproval") {
              // Filter for invoices that need approval (status is not "approved")
              // This includes pending, rejected, or any other non-approved status
              formattedData = formattedData.filter(
                (item) => item.approvalStatus && item.approvalStatus !== "approved"
              );
            }
            // Note: "notSynced" filtering is now done server-side via isSynced filter
            
            // Update pagination
            // For "needsApproval", we do client-side filtering so use filtered count
            // For other tabs, use server response pagination
            const finalCount = activeTab === "needsApproval" 
              ? formattedData.length 
              : response.pagination.totalCount;
            
            setPagination({
              ...response.pagination,
              totalCount: finalCount,
              totalPages: Math.ceil(finalCount / pagination.pageSize),
            });
            setHasData(finalCount > 0);
            
            // Update tab counts based on current tab
            setTabCounts((prev) => ({
              ...prev,
              [activeTab]: finalCount,
            }));
            
            params.successCallback(
              formattedData,
              finalCount
            );
          })
          .catch((err) => {
            params.failCallback();
            setHasData(false);
            dispatch(
              createAlert({
                message: err.message || "Error fetching invoices",
                timeout: 0,
                type: "error",
              })
            );
          });
      } catch (err) {
        params.failCallback();
        setHasData(false);
        dispatch(
          createAlert({
            message:
              err instanceof Error ? err.message : "Error fetching invoices",
            timeout: 0,
            type: "error",
          })
        );
      }
    },
    [job, pagination.pageSize, externalFilters, l1, l2, activeTab, hasIntegration]
  );

  const dataSource = useMemo(
    () => ({
      rowCount: undefined,
      getRows,
    }),
    [getRows]
  );

  // Reset hasData when tab changes to prevent showing empty state prematurely
  useEffect(() => {
    setHasData(null); // Set to loading state when tab changes
  }, [activeTab]);

  useEffect(() => {
    if (gridApi.current) {
      // console.log("purgeInfiniteCache");
      gridApi.current.purgeInfiniteCache();
      // Reset to first page when tab changes
      gridApi.current.paginationGoToPage(0);
      // Refresh the datasource when tab changes
      gridApi.current.setDatasource(dataSource);
    }
  }, [l1, l2, activeTab, dataSource]);

  const onGridReady = useCallback(
    (params: GridReadyEvent) => {
      gridApi.current = params.api;
      // Reset hasData to null when grid is ready (loading state)
      setHasData(null);
      // Set the datasource which will trigger getRows
      gridApi.current.setDatasource(dataSource);

      // Initialize external filters after grid is ready
      dispatch(clearExternalFilters());
      dispatch(
        setExternalFiltersColumnDefs({
          externalFilterColumnDefs: externalFilterColumns,
        })
      );
    },
    [dataSource, dispatch, externalFilterColumns]
  );

  const SelectInvoiceRenderer = (props: any) => {
    if (!props.data || !props.data.id) {
      return <></>;
    }
    const openInvoiceEditor = () => {
      dispatch(setSelectedInvoiceId(props.data.id));
      const searchParams = new URLSearchParams(location.search);
      searchParams.set("invoiceId", String(props.data.id));
      history.replace(`${location.pathname}?${searchParams.toString()}`);
    };

    const handleDeleteClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      // Keep delete action isolated from row click navigation.
      e.stopPropagation();
      const invoiceId = props.data.id;

      const getMessage = () => (
        <div style={{ marginBottom: "20px" }}>
          <span>{`Are you sure you want to delete invoice with ID: ${invoiceId}?`}</span>
        </div>
      );

      confirmAlert({
        customUI: getCustomUi({
          onConfirm: () => {
            deleteTransactionLine(parseInt(invoiceId))
              .then(() => {
                dispatch(
                  createAlert({
                    message: `Invoice with ID ${invoiceId} deleted successfully.`,
                    timeout: 3000,
                    type: "success",
                  })
                );
                if (gridApi.current) {
                  gridApi.current.purgeInfiniteCache();
                  gridApi.current.setDatasource({
                    rowCount: undefined,
                    getRows,
                  });
                }
              })
              .catch((err) => {
                dispatch(
                  createAlert({
                    message: err.message || "Error deleting invoice.",
                    timeout: 0,
                    type: "error",
                  })
                );
              });
          },
          confirmCTA: "Delete Invoice",
          cancelCTA: "Cancel",
          getMessage,
        }),
      });
    };

    return (
      <div className="d-flex align-items-center justify-content-center">
        <Button
          variant="link"
          size="sm"
          onClick={handleDeleteClick}
          className="text-danger"
          aria-label="Delete invoice"
        >
          <FontAwesomeIcon icon="trash" />
        </Button>
      </div>
    );
  };

  const handleInvoiceRowClick = useCallback(
    (event: RowClickedEvent) => {
      const invoiceId = event?.data?.id;
      if (!invoiceId) return;
      dispatch(setSelectedInvoiceId(invoiceId));
      const searchParams = new URLSearchParams(location.search);
      searchParams.set("invoiceId", String(invoiceId));
      history.replace(`${location.pathname}?${searchParams.toString()}`);
    },
    [dispatch, history, location.pathname, location.search]
  );

  const columnDefs = useMemo(
    () => [
      {
        field: "invoiceId",
        headerName: "Invoice ID",
        sortable: true,
        filter: true,
      },
      {
        field: "invoiceType",
        headerName: "Type",
        sortable: true,
        filter: true,
      },
      {
        field: "docType",
        headerName: "Document Type",
        sortable: true,
        filter: true,
      },
      {
        field: "invoiceVendor",
        headerName: "Vendor",
        sortable: true,
        filter: true,
      },
      {
        field: "invoiceCustomer",
        headerName: "Customer",
        sortable: true,
        filter: true,
      },
      {
        field: "invoiceAmount",
        headerName: "Amount",
        sortable: true,
        filter: "agNumberColumnFilter",
        valueFormatter: (params: any) =>
          params.value
            ? new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "USD",
              }).format(params.value)
            : "",
      },
      {
        field: "invoiceDate",
        headerName: "Date",
        sortable: true,
        filter: "agDateColumnFilter",
        filterParams: datefilterParamsYYYYMMDD,
      },
      {
        field: "approvalStatus",
        headerName: "Approval Status",
        sortable: true,
        filter: true,
        valueFormatter: (params: any) => {
          if (!params.value) return "";
          return params.value.charAt(0).toUpperCase() + params.value.slice(1);
        },
      },
      {
        field: "hasPOMatch",
        headerName: "Has PO Match",
        sortable: true,
        filter: true,
        valueFormatter: (params: any) => {
          if (!params.data) return "";
          return params.value === "true" ? "Yes" : "No";
        },
      },
      {
        field: "hasBankMatch",
        headerName: "Has Bank Match",
        sortable: true,
        filter: true,
        valueFormatter: (params: any) => {
          if (!params.data) return "";
          return params.value === "true" ? "Yes" : "No";
        },
      },
      {
        headerName: "Actions",
        cellRenderer: SelectInvoiceRenderer,
        sortable: false,
        filter: false,
        width: 90,
      },
    ],
    []
  );

  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      flex: 1,
      filter: true,
      sortable: true,
    }),
    []
  );

  const handleDocumentAction = () => {
    // Preserve current TagLevel context (l1/l2) so the extract flow routes correctly.
    const params = new URLSearchParams();
    if (l1) params.append("l1", l1);
    if (l2) params.append("l2", l2);

    history.push(
      `/extract/invoice${params.toString() ? `?${params.toString()}` : ""}`
    );
  };

  // Empty State Component
  const EmptyStateComponent = () => (
    <div
      className="d-flex flex-column align-items-center justify-content-center"
      style={{ minHeight: "400px" }}
    >
      <div className="text-center mb-4">
        <FontAwesomeIcon
          icon="file-invoice"
          size="4x"
          className="text-muted mb-3"
        />
        <h4 className="text-muted">No Invoices Found</h4>
        <p className="text-muted">
          Get started by adding your invoices, receipts, or bills
        </p>
        <Button
          variant="primary"
          size="sm"
          className="mt-2"
          onClick={handleDocumentAction}
        >
          <FontAwesomeIcon icon="file-invoice" className="mr-2" />
          Upload Invoices & Receipts
        </Button>
      </div>
      <div className="mt-3 text-center">
        <small className="text-muted d-block mb-2">
          Don't have a PDF handy?
        </small>
        <TourMenuButton variant="inline" label="Take a 2-minute tour" />
      </div>
    </div>
  );

  // Check if we should show empty state (only when no filters are applied and no data)
  // Don't show empty state if we're loading (hasData === null)
  const showEmptyState = hasData === false && Object.keys(externalFilters).length === 0 && hasData !== null;

  const inboxSummary = inboxSummaryData;

  const applyApQueueUiConfig = useCallback((uiConfig?: any) => {
    if (!uiConfig || typeof uiConfig !== "object") return;
    setApQueueConfig((prev) => ({
      ...prev,
      ignore_po_matching: Boolean(uiConfig.ignore_po_matching),
      ignore_bank_matching: Boolean(uiConfig.ignore_bank_matching),
    }));
  }, []);

  const filteredInboxRows = useMemo(() => {
    const byTab = inboxRows.filter((row) => {
      if (activeInboxTab === "all") return true;
      if (activeInboxTab === "approved") return row.workflowStatus === "approved";
      if (activeInboxTab === "exceptions") return row.hasIssues;
      return row.workflowStatus !== "paid" || row.hasIssues;
    });

    return [...byTab].sort((a, b) => {
      if (a.hasIssues !== b.hasIssues) return a.hasIssues ? -1 : 1;
      if (a.isOverdue !== b.isOverdue) return a.isOverdue ? -1 : 1;
      if ((a.invoiceAmount || 0) !== (b.invoiceAmount || 0)) {
        return (b.invoiceAmount || 0) - (a.invoiceAmount || 0);
      }
      const aDue = a.dueDate ? new Date(a.dueDate).getTime() : Number.MAX_SAFE_INTEGER;
      const bDue = b.dueDate ? new Date(b.dueDate).getTime() : Number.MAX_SAFE_INTEGER;
      return aDue - bDue;
    });
  }, [inboxRows, activeInboxTab]);

  const inboxTabCounts = useMemo(() => {
    return {
      inbox: inboxRows.filter((row) => row.workflowStatus !== "paid" || row.hasIssues).length,
      exceptions: inboxRows.filter((row) => row.hasIssues).length,
      approved: inboxRows.filter((row) => row.workflowStatus === "approved").length,
      all: inboxRows.length,
    };
  }, [inboxRows]);

  const formatCurrency = useCallback((value?: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 2,
    }).format(Number(value || 0));
  }, []);

  const loadAnalyticsData = useCallback(async () => {
    if (!l1 || !l2) return;
    try {
      setAnalyticsLoading(true);
      setAnalyticsError(null);
      const data = await getInvoiceAnalyticsTagLevels(l1, l2, {
        interval: analyticsInterval,
        startDate: analyticsStartDate || undefined,
        endDate: analyticsEndDate || undefined,
        topVendors: 5,
      });
      setAnalyticsData(data || null);
    } catch (err) {
      setAnalyticsError(err instanceof Error ? err.message : "Error loading analytics");
    } finally {
      setAnalyticsLoading(false);
    }
  }, [l1, l2, analyticsInterval, analyticsStartDate, analyticsEndDate]);

  const loadInboxSummary = useCallback(async () => {
    if (!l1 || !l2) return;
    try {
      const summary = await getInvoiceWorkflowSummaryTagLevels(l1, l2);
      applyApQueueUiConfig(summary?.uiConfig);
      setInboxSummaryData({
        needsReviewCount: Number(summary?.needsReviewCount || 0),
        exceptionsCount: Number(summary?.exceptionsCount || 0),
        overdueCount: Number(summary?.overdueCount || 0),
        totalPendingAmount: Number(summary?.totalPendingAmount || 0),
        firstTimeIssueCount: Number(summary?.firstTimeIssueCount || 0),
      });
    } catch (err) {
      setInboxError(err instanceof Error ? err.message : "Error loading inbox");
    }
  }, [l1, l2]);

  const loadInboxData = useCallback(async () => {
    if (!l1 || !l2) return;
    try {
      setInboxLoading(true);
      setInboxError(null);
      const response = await getInvoiceWorkflowTagLevels(
        l1,
        l2,
        activeInboxTab,
        1,
        300
      );
      applyApQueueUiConfig(response?.uiConfig);
      const rows = (response?.data || []).map((row: any) => ({
        ...row,
        id: row.transactionLineId,
        invoiceVendor: row.vendorNormalized || "",
        invoiceAmount: Number(row.amount || 0),
        dueDate: row.dueDate || null,
      }));
      setInboxRows(rows);
    } catch (err) {
      setInboxError(err instanceof Error ? err.message : "Error loading inbox");
    } finally {
      setInboxLoading(false);
    }
  }, [l1, l2, activeInboxTab, applyApQueueUiConfig]);

  const updateApQueueConfigPreference = useCallback(
    async (
      key: "ignore_po_matching" | "ignore_bank_matching",
      checked: boolean
    ) => {
      setSavingApQueueConfig((prev) => ({ ...prev, [key]: true }));
      try {
        await saveContractdPreferenceServer({
          key,
          value: checked ? "true" : "false",
        });
        setApQueueConfig((prev) => ({ ...prev, [key]: checked }));
        await Promise.all([loadInboxSummary(), loadInboxData()]);
      } catch (err) {
        dispatch(
          createAlert({
            message: "Unable to save AP queue config",
            timeout: 4000,
            type: "error",
          })
        );
      } finally {
        setSavingApQueueConfig((prev) => ({ ...prev, [key]: false }));
      }
    },
    [dispatch, loadInboxData, loadInboxSummary]
  );

  useEffect(() => {
    if (viewMode === "inbox") {
      loadInboxSummary();
      loadInboxData();
    }
    if (viewMode === "analytics") {
      loadAnalyticsData();
    }
  }, [viewMode, loadInboxData, loadInboxSummary, loadAnalyticsData]);

  const closeInlineEditor = () => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.delete("invoiceId");
    const qs = searchParams.toString();
    history.replace(qs ? `${location.pathname}?${qs}` : location.pathname);
  };

  if (editingInvoiceId) {
    return (
      <div className="p-4">
        <Button variant="link" size="sm" className="mb-3 pl-0" onClick={closeInlineEditor}>
          <FontAwesomeIcon icon="arrow-left" className="mr-1" />
          {viewMode === "inbox" ? "Back to AP Queue" : "Back to list"}
        </Button>
        <InvoicePage />
      </div>
    );
  }

  if (!modePreferenceLoaded) {
    return (
      <div className="p-4 d-flex align-items-center">
        <Spinner animation="border" size="sm" className="mr-2" />
        <small className="text-muted">Loading invoice experience...</small>
      </div>
    );
  }

  return (
    <div className="p-4">
      {showModeToggle && (
        <Row className="mb-3">
          <Col className="d-flex align-items-center justify-content-between">
            <Nav
              variant="pills"
              activeKey={viewMode}
              onSelect={async (k) => {
                const nextMode = ((k as ViewMode) || "invoices") as ViewMode;
                if (nextMode === viewMode) return;
                if (!isContractAdmin) return;
                try {
                  await saveContractdPreferenceServer({
                    key: "invoiceExperienceMode",
                    value: nextMode,
                  });
                  setViewMode(nextMode);
                } catch (err) {
                  dispatch(
                    createAlert({
                      message: "Unable to save invoice mode preference",
                      timeout: 4000,
                      type: "error",
                    })
                  );
                }
              }}
            >
              <NavItem>
                <NavLink eventKey="invoices" data-tour-key="invoice-view-list" disabled={!isContractAdmin || !modePreferenceLoaded}>
                  Invoices
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink eventKey="inbox" data-tour-key="invoice-view-apq" disabled={!isContractAdmin || !modePreferenceLoaded}>
                  AP Queue
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink eventKey="analytics" data-tour-key="invoice-view-analytics" disabled={!isContractAdmin || !modePreferenceLoaded}>
                  Analytics
                </NavLink>
              </NavItem>
            </Nav>
            {!isContractAdmin && (
              <small className="text-muted">
                Only admins can change this view
              </small>
            )}
          </Col>
        </Row>
      )}
      {viewMode === "inbox" ? (
        <div data-tour-key="invoice-view-apq">
          {showInboxEntryMessage && (
            <Alert variant="success" dismissible onClose={() => setShowInboxEntryMessage(false)}>
              We found <strong>{inboxSummary.exceptionsCount}</strong> invoices that may need review.
            </Alert>
          )}
          <Row className="mb-3">
            <Col md="3">
              <Card body>
                <small className="text-muted">Needs review</small>
                <h4 className="mb-0">{inboxSummary.needsReviewCount}</h4>
              </Card>
            </Col>
            <Col md="3">
              <Card body>
                <small className="text-muted">Exceptions</small>
                <h4 className="mb-0">{inboxSummary.exceptionsCount}</h4>
              </Card>
            </Col>
            <Col md="3">
              <Card body>
                <small className="text-muted">Overdue</small>
                <h4 className="mb-0">{inboxSummary.overdueCount}</h4>
              </Card>
            </Col>
            <Col md="3">
              <Card body>
                <small className="text-muted">Total pending</small>
                <h4 className="mb-0">
                  {new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                  }).format(inboxSummary.totalPendingAmount || 0)}
                </h4>
              </Card>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md="12">
              <Nav
                variant="tabs"
                activeKey={activeInboxTab}
                onSelect={(k) => setActiveInboxTab((k as InboxTabType) || "inbox")}
              >
                <NavItem><NavLink eventKey="inbox">AP Queue <Badge variant="light">{inboxTabCounts.inbox}</Badge></NavLink></NavItem>
                <NavItem><NavLink eventKey="exceptions">Exceptions <Badge variant="light">{inboxTabCounts.exceptions}</Badge></NavLink></NavItem>
                <NavItem><NavLink eventKey="approved">Approved <Badge variant="light">{inboxTabCounts.approved}</Badge></NavLink></NavItem>
                <NavItem><NavLink eventKey="all">All <Badge variant="light">{inboxTabCounts.all}</Badge></NavLink></NavItem>
              </Nav>
            </Col>
          </Row>
          {isContractAdmin && (
            <Row className="mb-3">
              <Col md="12" className="d-flex flex-wrap" style={{ gap: "1rem" }}>
                <Form.Check
                  type="switch"
                  id="ap-ignore-po-matching"
                  label="Ignore PO matching"
                  checked={apQueueConfig.ignore_po_matching}
                  disabled={savingApQueueConfig.ignore_po_matching}
                  onChange={(e) =>
                    updateApQueueConfigPreference(
                      "ignore_po_matching",
                      e.target.checked
                    )
                  }
                />
                <Form.Check
                  type="switch"
                  id="ap-ignore-bank-matching"
                  label="Ignore bank matching"
                  checked={apQueueConfig.ignore_bank_matching}
                  disabled={savingApQueueConfig.ignore_bank_matching}
                  onChange={(e) =>
                    updateApQueueConfigPreference(
                      "ignore_bank_matching",
                      e.target.checked
                    )
                  }
                />
              </Col>
            </Row>
          )}
          <Row>
            <Col md="12">
              {inboxLoading && <div>Loading AP Queue...</div>}
              {inboxError && <Alert variant="danger">{inboxError}</Alert>}
              {!inboxLoading && !inboxError && (
                <div style={{ maxHeight: "550px", overflow: "auto" }}>
                  <Table
                    hover
                    responsive
                    className="w-100"
                    style={{ width: "100%", marginLeft: 0, marginRight: 0 }}
                  >
                    <thead>
                      <tr>
                        <th className="text-left">Vendor</th>
                        <th className="text-left">Amount</th>
                        <th className="text-left">Due Date</th>
                        <th className="text-left">Status</th>
                        <th className="text-left">Flags</th>
                        {hasIntegration && <th className="text-left">Sync</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredInboxRows.map((row: any) => {
                        const flags = (row.flags || []) as string[];
                        const labels = flags
                          .map((flag) => {
                            if (flag === "po_mismatch") {
                              return apQueueConfig.ignore_po_matching ? null : "PO";
                            }
                            if (flag === "bank_mismatch") {
                              return apQueueConfig.ignore_bank_matching ? null : "Bank";
                            }
                            if (flag === "missing_data") return "Missing";
                            if (flag === "duplicate") return "Duplicate";
                            return flag;
                          })
                          .filter(Boolean) as string[];
                        if (
                          !apQueueConfig.ignore_bank_matching &&
                          !row.hasBankMatch &&
                          !labels.includes("Bank")
                        ) {
                          labels.push("Bank");
                        }
                        if (row.workflowStatus === "needs_review") labels.push("Review");
                        const uniqueLabels = Array.from(new Set(labels)).join(", ");

                        return (
                          <tr
                            key={row.id}
                            style={{ cursor: "pointer" }}
                            onClick={() => {
                              const searchParams = new URLSearchParams(location.search);
                              searchParams.set("invoiceId", String(row.id));
                              history.replace(`${location.pathname}?${searchParams.toString()}`);
                            }}
                          >
                            <td className="text-left">{row.invoiceVendor || "-"}</td>
                            <td className="text-left" style={{ fontWeight: 700 }}>
                              {new Intl.NumberFormat("en-US", {
                                style: "currency",
                                currency: "USD",
                              }).format(row.invoiceAmount || 0)}
                            </td>
                            <td
                              className="text-left"
                              style={row.isOverdue ? { color: "#dc3545", fontWeight: 600 } : {}}
                            >
                              {row.dueDate || "-"}
                            </td>
                            <td className="text-left">
                              {String(row.workflowStatus || "")
                                .replace(/_/g, " ")
                                .replace(/\b\w/g, (s) => s.toUpperCase())}
                            </td>
                            <td
                              className="text-left"
                              style={uniqueLabels ? { color: "#dc3545", fontWeight: 600 } : {}}
                            >
                              {uniqueLabels || "-"}
                            </td>
                            {hasIntegration && (
                              <td className="text-left">
                                {row.workflowStatus === "approved" ? "Ready to Sync" : "-"}
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              )}
            </Col>
          </Row>
        </div>
      ) : viewMode === "analytics" ? (
        <div data-tour-key="invoice-view-analytics">
          <Row className="mb-3">
            <Col md="3">
              <Form.Group>
                <Form.Label>Time Interval</Form.Label>
                <Form.Control
                  as="select"
                  value={analyticsInterval}
                  onChange={(e) =>
                    setAnalyticsInterval((e.target.value as "week" | "month") || "month")
                  }
                >
                  <option value="month">Monthly</option>
                  <option value="week">Weekly</option>
                </Form.Control>
              </Form.Group>
            </Col>
            <Col md="3">
              <Form.Group>
                <Form.Label>Start Date</Form.Label>
                <Form.Control
                  type="date"
                  value={analyticsStartDate}
                  onChange={(e) => setAnalyticsStartDate(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md="3">
              <Form.Group>
                <Form.Label>End Date</Form.Label>
                <Form.Control
                  type="date"
                  value={analyticsEndDate}
                  onChange={(e) => setAnalyticsEndDate(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md="3" className="d-flex align-items-end">
              <Button
                variant="primary"
                className="w-100"
                onClick={loadAnalyticsData}
                disabled={analyticsLoading}
              >
                {analyticsLoading ? "Loading..." : "Apply Filters"}
              </Button>
            </Col>
          </Row>
          {analyticsError && <Alert variant="danger">{analyticsError}</Alert>}
          <Row className="mb-3">
            <Col md="3">
              <Card body>
                <small className="text-muted">Total Spend</small>
                <h4 className="mb-0">{formatCurrency(analyticsData?.totals?.totalSpend)}</h4>
              </Card>
            </Col>
            <Col md="3">
              <Card body>
                <small className="text-muted">Average Invoice</small>
                <h4 className="mb-0">
                  {formatCurrency(analyticsData?.totals?.averageInvoiceAmount)}
                </h4>
              </Card>
            </Col>
            <Col md="3">
              <Card body>
                <small className="text-muted">Invoice Volume</small>
                <h4 className="mb-0">{Number(analyticsData?.totals?.invoiceCount || 0)}</h4>
              </Card>
            </Col>
            <Col md="3">
              <Card body>
                <small className="text-muted">Potential Duplicates</small>
                <h4 className="mb-0">{Number(analyticsData?.duplicateSuspicious?.count || 0)}</h4>
              </Card>
            </Col>
          </Row>
          <Row className="mb-4">
            <Col md="6">
              <Card body>
                <h6>Spend Over Time</h6>
                <div style={{ height: 260 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={analyticsData?.spendOverTime || []}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="period" />
                      <YAxis />
                      <Tooltip formatter={(v: any) => formatCurrency(Number(v || 0))} />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="spend"
                        stroke="#007bff"
                        strokeWidth={2}
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </Card>
            </Col>
            <Col md="6">
              <Card body>
                <h6>Invoice Volume Over Time</h6>
                <div style={{ height: 260 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={analyticsData?.invoiceVolumeOverTime || []}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="period" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="invoiceCount" fill="#28a745" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Card>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md="6">
              <Card body>
                <h6>Top Vendors</h6>
                <Table size="sm" hover responsive>
                  <thead>
                    <tr>
                      <th>Vendor</th>
                      <th>Spend</th>
                      <th>Invoices</th>
                      <th>Avg</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(analyticsData?.topVendors || []).map((vendor: any) => (
                      <tr key={`${vendor.vendor}-${vendor.totalSpend}`}>
                        <td>{vendor.vendor}</td>
                        <td>{formatCurrency(Number(vendor.totalSpend || 0))}</td>
                        <td>{Number(vendor.invoiceCount || 0)}</td>
                        <td>{formatCurrency(Number(vendor.averageInvoiceAmount || 0))}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card>
            </Col>
            <Col md="6">
              <Card body className="mb-3">
                <h6>Status Breakdown</h6>
                <Table size="sm" hover responsive>
                  <thead>
                    <tr>
                      <th>Status</th>
                      <th>Count</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Unpaid</td>
                      <td>{Number(analyticsData?.statusBreakdown?.unpaid?.count || 0)}</td>
                      <td>{formatCurrency(Number(analyticsData?.statusBreakdown?.unpaid?.amount || 0))}</td>
                    </tr>
                    <tr>
                      <td>Overdue</td>
                      <td>{Number(analyticsData?.statusBreakdown?.overdue?.count || 0)}</td>
                      <td>{formatCurrency(Number(analyticsData?.statusBreakdown?.overdue?.amount || 0))}</td>
                    </tr>
                  </tbody>
                </Table>
              </Card>
              <Card body>
                <h6>Aging Report</h6>
                <Table size="sm" hover responsive>
                  <thead>
                    <tr>
                      <th>Bucket</th>
                      <th>Count</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>0-30 days</td>
                      <td>{Number(analyticsData?.agingReport?.bucket0to30?.count || 0)}</td>
                      <td>{formatCurrency(Number(analyticsData?.agingReport?.bucket0to30?.amount || 0))}</td>
                    </tr>
                    <tr>
                      <td>31-60 days</td>
                      <td>{Number(analyticsData?.agingReport?.bucket31to60?.count || 0)}</td>
                      <td>{formatCurrency(Number(analyticsData?.agingReport?.bucket31to60?.amount || 0))}</td>
                    </tr>
                    <tr>
                      <td>60+ days</td>
                      <td>{Number(analyticsData?.agingReport?.bucket60Plus?.count || 0)}</td>
                      <td>{formatCurrency(Number(analyticsData?.agingReport?.bucket60Plus?.amount || 0))}</td>
                    </tr>
                  </tbody>
                </Table>
              </Card>
            </Col>
          </Row>
        </div>
      ) : (
        <>
      {l1 && l2 && (
        <Row className="justify-content-end mb-3">
          <Col className="d-flex justify-content-end">
            <Button
              variant="primary"
              size="sm"
              onClick={() => dispatch(setDownloadInvoiceModalOpen(true))}
            >
              <FontAwesomeIcon icon="download" className="mx-2" />
              Download Invoices
            </Button>
            <DownloadInvoiceModal />
          </Col>
        </Row>
      )}
      <Row className="mb-3">
        <Col md="12">
          <Row className="px-3">
            <span className="ml-2 mr-2">Filter by</span>
            {gridApi.current && (
              <ExternalFilter
                columnDefs={externalFilterColumns}
                gridApi={gridApi.current}
              />
            )}
          </Row>
        </Col>
      </Row>

      {/* Show empty state if no data and no filters, otherwise show grid */}
      {showEmptyState ? (
        <Row>
          <Col md="12">
            <EmptyStateComponent />
          </Col>
        </Row>
      ) : (
        <>
          {hasData === false && Object.keys(externalFilters).length > 0 && (
            <Row className="mb-3">
              <Col md="12">
                <div className="alert alert-info text-center">
                  <FontAwesomeIcon icon="info-circle" className="mr-2" />
                  No invoices match the current filters. Try adjusting your filters or switch to a different tab.
                </div>
              </Col>
            </Row>
          )}
          <div
            className="ag-theme-alpine"
            style={{ height: "500px", width: "100%" }}
          >
            <AgGridReact
              key={`invoice-grid-${activeTab}-${l1}-${l2}`}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              onGridReady={onGridReady}
              onRowClicked={handleInvoiceRowClick}
              getRowStyle={() => ({ cursor: "pointer" })}
              suppressReactUi={true}
              rowModelType="infinite"
              cacheBlockSize={pagination.pageSize}
              cacheOverflowSize={2}
              maxConcurrentDatasourceRequests={1}
              infiniteInitialRowCount={0}
              maxBlocksInCache={10}
              pagination={true}
              paginationPageSize={pagination.pageSize}
              suppressCellFocus={true}
            />
          </div>
        </>
      )}
        </>
      )}
    </div>
  );
};

export default Invoices;
